import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Modal, Button } from "react-bootstrap";
import { FaMapMarkerAlt, FaRoad } from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import COLORS from "../resources/colors/COLORS";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

function formFromPoint(point) {
  return {
    name: String(point?.pickup_point_name || ""),
    distance: String(point?.distance_from_school ?? ""),
  };
}

const MdlEditPickupPoint = ({ routeId, point, actionClose, onSuccess }) => {
  const [show, setShow] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(() => formFromPoint(point));

  useEffect(() => {
    setShow(true);
    setForm(formFromPoint(point));
    setSaving(false);
  }, [point]);

  const handleClose = useCallback(() => {
    if (saving) return;
    setShow(false);
    if (typeof actionClose === "function") actionClose();
  }, [actionClose, saving]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleSubmit = useCallback(async () => {
    const pickupPointId = point?.pickup_point_id;
    if (!routeId || !pickupPointId) {
      myToast.notify_warning("Pickup point id is missing.");
      return;
    }

    const name = form.name.trim();
    const distance = String(form.distance).trim();
    if (!name) {
      myToast.notify_warning("Enter pickup point name.");
      return;
    }
    if (
      distance !== "" &&
      (!Number.isFinite(Number(distance)) || Number(distance) < 0)
    ) {
      myToast.notify_warning("Enter a valid distance, or leave it blank.");
      return;
    }

    const schoolCode = SessionManager.getSchoolCode();
    if (!schoolCode) {
      myToast.notify_warning("School code is missing.");
      return;
    }

    setSaving(true);
    try {
      const params = new URLSearchParams();
      params.append("school_code", schoolCode);
      params.append("route_id", routeId);
      params.append("pickup_point_id", pickupPointId);
      params.append("pickup_point_name", name);
      params.append("distance_from_school", distance);

      const response = await axios.post(
        `${API_BASE()}updatePickupPoint.php`,
        params
      );

      if (response.data?.api_status === "OK") {
        myToast.notify_success("Pickup point updated");
        setShow(false);
        if (typeof onSuccess === "function") {
          onSuccess({
            ...point,
            pickup_point_name: name,
            distance_from_school: distance,
          });
        }
        if (typeof actionClose === "function") actionClose();
      } else {
        myToast.notify_warning(
          response.data?.api_remark || "Could not update pickup point"
        );
      }
    } catch (error) {
      console.error("[MdlEditPickupPoint] update error", error);
      myToast.notify_error("Failed to update pickup point");
    } finally {
      setSaving(false);
    }
  }, [form, point, routeId, actionClose, onSuccess]);

  return (
    <Modal
      show={show}
      onHide={handleClose}
      animation={false}
      centered
      size="lg"
      className="mdl-add-route"
      backdrop={saving ? "static" : true}
      keyboard={!saving}
    >
      <Modal.Header closeButton={!saving} className="mdl-add-route__header">
        <Modal.Title className="mdl-add-route__title">
          <span className="mdl-add-route__icon" aria-hidden>
            <FaMapMarkerAlt />
          </span>
          Edit Pickup Point
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="mdl-add-route__body">
        {saving ? (
          <div className="mdl-add-route__loading">
            <MyLoader />
          </div>
        ) : (
          <div className="mdl-add-route__form">
            <div className="mdl-add-route__field">
              <label htmlFor="mdl-edit-pickup-name">
                <FaMapMarkerAlt aria-hidden /> Pickup name
              </label>
              <input
                id="mdl-edit-pickup-name"
                name="name"
                type="text"
                placeholder="e.g. City Center Stop"
                value={form.name}
                onChange={handleChange}
                autoFocus
              />
            </div>
            <div className="mdl-add-route__field">
              <label htmlFor="mdl-edit-pickup-distance">
                <FaRoad aria-hidden /> Distance from school (km)
                <span className="mdl-add-route__optional"> optional</span>
              </label>
              <input
                id="mdl-edit-pickup-distance"
                name="distance"
                type="number"
                min="0"
                step="0.1"
                placeholder="e.g. 3.5 (optional)"
                value={form.distance}
                onChange={handleChange}
              />
            </div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="mdl-add-route__footer">
        <Button
          variant="light"
          onClick={handleClose}
          disabled={saving}
          className="mdl-add-route__cancel"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={saving}
          className="mdl-add-route__submit"
          style={{
            background: COLORS.primary_color_shade5,
            borderColor: COLORS.primary_color_shade7,
          }}
        >
          {saving ? "Saving…" : "Save Changes"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default MdlEditPickupPoint;
